import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Drop the Firebase Storage URL here when Runway delivers ───────────────────
const INTRO_VIDEO_URL = ''; // e.g. 'https://firebasestorage.googleapis.com/.../intro.mp4?alt=media'
// ────────────────────────────────────────────────────────────────────────────────

export default function StartPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const hasVideo = Boolean(INTRO_VIDEO_URL);

  function handlePlay() {
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.play();
      setPlaying(true);
    }
  }

  function toggleMute() {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  }

  return (
    <div style={styles.page}>

      {/* ── Hero ── */}
      <div style={styles.hero}>

        {/* Video layer */}
        {hasVideo ? (
          <video
            ref={videoRef}
            src={INTRO_VIDEO_URL}
            style={styles.video}
            autoPlay
            muted
            playsInline
            loop={false}
            onEnded={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
        ) : (
          /* Placeholder while video renders */
          <div style={styles.videoPlaceholder}>
            <div style={styles.placeholderFace}>
              <img
                src="https://firebasestorage.googleapis.com/v0/b/operator-method.firebasestorage.app/o/images%2FAI_close-up-cyan.png?alt=media"
                alt="AI Instructor"
                style={styles.placeholderImg}
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          </div>
        )}

        {/* Gradient overlay — bottom fade into content */}
        <div style={styles.heroOverlay} />

        {/* Video controls — only shown when video is loaded */}
        {hasVideo && (
          <div style={styles.videoControls}>
            <button onClick={handlePlay} style={styles.controlBtn}>
              {playing ? '⏸' : '▶'}
            </button>
            <button onClick={toggleMute} style={styles.controlBtn}>
              {muted ? '🔇' : '🔊'}
            </button>
          </div>
        )}

        {/* Hero copy — sits over the video */}
        <div style={styles.heroCopy}>
          <div style={styles.eyebrow}>The Operator Method</div>
          <h1 style={styles.headline}>
            I don't know what brought you here.
          </h1>
          <p style={styles.subhead}>
            But take a minute and let me show you what you stumbled on.
          </p>
        </div>
      </div>

      {/* ── Body copy ── */}
      <div style={styles.body}>

        <div style={styles.section}>
          <p style={styles.p}>
            Most people are using AI the same way you use a search engine.
            Type something in. Hope for something useful. Get frustrated when it misses.
          </p>
          <p style={styles.p}>
            That's not a tool problem. That's a language problem.
          </p>
        </div>

        <div style={styles.divider} />

        <div style={styles.section}>
          <p style={styles.p}>
            AI has a language. A structure. A way it receives instructions and decides
            how seriously to take them. Nobody taught you that language — because until
            recently, nobody needed to know it.
          </p>
          <p style={styles.p}>
            <strong style={{ color: '#f0f4ff' }}>You need to know it now.</strong>
          </p>
        </div>

        <div style={styles.divider} />

        <div style={styles.section}>
          <p style={styles.p}>
            The Operator Method is a one-on-one AI instructor that meets you exactly
            where you are. No lectures. No videos to skip through.
            It asks you questions. It listens to how you respond.
            And it teaches you based on what you actually know — not what you think you know.
          </p>
        </div>

        {/* CTA */}
        <div style={styles.ctaBlock}>
          <button onClick={() => navigate('/discover')} style={styles.ctaBtn}>
            Find out where you stand →
          </button>
          <p style={styles.ctaNote}>Takes 2 minutes. No account required.</p>
        </div>

      </div>

      {/* ── Footer strip ── */}
      <div style={styles.footer}>
        <span style={styles.footerLogo}>THE OPERATOR METHOD</span>
        <span style={styles.footerLinks}>
          <span onClick={() => navigate('/privacy')} style={styles.footerLink}>Privacy</span>
          <span onClick={() => navigate('/terms')} style={styles.footerLink}>Terms</span>
          <span onClick={() => navigate('/login')} style={styles.footerLink}>Sign in</span>
        </span>
      </div>

    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#c8d0e0',
    display: 'flex',
    flexDirection: 'column',
  },
  // ── Hero
  hero: {
    position: 'relative',
    width: '100%',
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    backgroundColor: '#040710',
  },
  video: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
  },
  videoPlaceholder: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#040710',
  },
  placeholderFace: {
    width: '260px',
    height: '260px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '1px solid #1e2a3a',
  },
  placeholderImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
    filter: 'brightness(0.85)',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(4,7,16,0.1) 0%, rgba(4,7,16,0.55) 60%, rgba(10,14,26,1) 100%)',
    pointerEvents: 'none',
  },
  videoControls: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    gap: '8px',
    zIndex: 5,
  },
  controlBtn: {
    background: 'rgba(10,14,26,0.7)',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    color: '#f0f4ff',
    fontSize: '16px',
    padding: '6px 10px',
    cursor: 'pointer',
  },
  heroCopy: {
    position: 'relative',
    zIndex: 2,
    padding: '0 32px 48px',
    maxWidth: '680px',
    margin: '0 auto',
    width: '100%',
    textAlign: 'center',
  },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: '#4f8ef7',
    marginBottom: '16px',
  },
  headline: {
    fontSize: 'clamp(28px, 5vw, 48px)',
    fontWeight: 700,
    color: '#f0f4ff',
    margin: '0 0 16px 0',
    lineHeight: 1.2,
  },
  subhead: {
    fontSize: 'clamp(16px, 2.5vw, 20px)',
    color: '#8a9bbf',
    margin: 0,
    lineHeight: 1.6,
  },
  // ── Body
  body: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '56px 32px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  section: {
    padding: '8px 0 24px',
  },
  p: {
    fontSize: '18px',
    lineHeight: 1.85,
    color: '#8a9bbf',
    margin: '0 0 20px 0',
  },
  divider: {
    height: '1px',
    backgroundColor: '#1e2a3a',
    margin: '8px 0 32px',
  },
  ctaBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '24px',
  },
  ctaBtn: {
    padding: '18px 48px',
    backgroundColor: '#4f8ef7',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '17px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.02em',
    width: '100%',
    maxWidth: '400px',
  },
  ctaNote: {
    fontSize: '13px',
    color: '#3a4560',
    margin: 0,
  },
  // ── Footer
  footer: {
    marginTop: 'auto',
    padding: '20px 32px',
    borderTop: '1px solid #1e2a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  footerLogo: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: '#2a3a50',
  },
  footerLinks: {
    display: 'flex',
    gap: '20px',
  },
  footerLink: {
    fontSize: '13px',
    color: '#2a3a50',
    cursor: 'pointer',
  },
};
